import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import disbursementsAPI from '../../api/disbursementsAPI';
import { useAuth } from '../../context/AuthContext';
import useCompanyContext from '../../hooks/useCompanyContext';

const DisbursementsList = () => {
  const navigate = useNavigate();
  const { selectedClient } = useAuth();
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();
  const accountingClientId = contextAccountingClientId || selectedClient?._id || null;
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    payeeType: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchDisbursements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters, accountingClientId]);

  useEffect(() => {
    // Prevent stale-company rows while new context is loading.
    setDisbursements([]);
    setPage(1);
  }, [accountingClientId]);

  const fetchDisbursements = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        limit,
        ...(accountingClientId && { accountingClient: accountingClientId }),
        ...(filters.status && { status: filters.status }),
        ...(filters.payeeType && { payeeType: filters.payeeType }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      };

      const res = await disbursementsAPI.getAll(params);
      setDisbursements(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch disbursements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      payeeType: '',
      startDate: '',
      endDate: ''
    });
    setPage(1);
  };

  const handleNewDisbursement = () => {
    navigate('/disbursements/new');
  };

  const handleRowClick = (item, e) => {
    if (!e.target.closest('button')) {
      handleEditDisbursement(item._id);
    }
  };

  const handleEditDisbursement = (id) => {
    navigate(`/disbursements/${id}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      posted: 'bg-green-100 text-green-800',
      voided: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const getTotalDebit = (lines) => {
    return (lines || []).reduce((sum, line) => sum + (line.debit || 0), 0);
  };

  return (
    <Layout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Disbursement Vouchers</h1>
            <button
              onClick={handleNewDisbursement}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
            >
              New Disbursement
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="posted">Posted</option>
                  <option value="voided">Voided</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payee Type
                </label>
                <select
                  name="payeeType"
                  value={filters.payeeType}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Types</option>
                  <option value="vendor">Vendor</option>
                  <option value="customer">Customer</option>
                  <option value="employee">Employee</option>
                  <option value="government">Government</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-6 text-center text-gray-500">Loading...</div>
            ) : disbursements.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No disbursements found</div>
            ) : (
              <>
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Reference No</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Payee</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disbursements.map(dv => (
                      <tr 
                        key={dv._id} 
                        onClick={(e) => handleRowClick(dv, e)}
                        className="border-b hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{dv.referenceNo}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{formatDate(dv.date)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{dv.payeeSnapshot?.name || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{formatCurrency(getTotalDebit(dv.lines))}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(dv.status)}`}>
                            {dv.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          {dv.status === 'draft' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditDisbursement(dv._id);
                              }}
                              className="text-teal-600 hover:text-teal-900 inline-flex items-center"
                              title="Edit draft disbursement"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                          {dv.status !== 'draft' && (
                            <span className="text-gray-400">
                              <PencilIcon className="h-5 w-5" />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} records
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-600">{page}</span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DisbursementsList;
